<template>
  <div>
    <div class="container-md">
      <p class="text-lead" data-testid="lead">
        導入医療機関としての掲載許可を頂いた医療機関を掲載しています。
        <br />
        {{ lastUpdatedAt ? `${lastUpdatedAt}時点` : '' }}
      </p>
    </div>
    <p v-if="loading">読み込み中…</p>
    <p v-else-if="!hasMedicalInstitutions">医療機関が見つかりませんでした。</p>
    <div v-else class="medical_institution-panel">
      <ul class="medical_institution-list">
        <li
          v-for="(
            medicalInstitutions, prefecture
          ) in prefectureGroupedMedicalInstitutions"
          :key="prefecture"
          class="medical_institution-item"
        >
          <p class="medical_institution-prefecture">{{ prefecture }}</p>
          <ul class="medical_institution-list-child">
            <li
              v-for="(
                medicalInstitution, medicalInstitutionIndex
              ) in medicalInstitutions"
              :key="medicalInstitutionIndex"
              class="medical_institution-child-item"
              :data-anchor="medicalInstitution.slug"
            >
              <p class="medical_institution-name">
                {{ medicalInstitution.name }}
              </p>
              <p class="medical_institution-address">
                〒{{ medicalInstitution.zipCode1 }}-{{
                  medicalInstitution.zipCode2
                }}
                {{ medicalInstitution.address }}
              </p>
              <ul class="medical_institution-label">
                <li
                  v-if="medicalInstitution.enableMedicalAppointment"
                  class="medical_institution-label-item bg-green"
                >
                  やくばと病院予約
                </li>
                <li
                  v-if="medicalInstitution.enablePharmacyReservation"
                  class="medical_institution-label-item bg-yellow"
                >
                  やくばと薬局予約
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>
      <p class="text-right">以上</p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeMount, ref } from 'vue';
import {
  MedicalInstitution,
  useMedicalInstitutionList,
} from '../stores/medical_institution/list';
import { formatByString } from '../utils/date';

type PrefectureGroupedMedicalInstitutions = Record<
  string,
  MedicalInstitution[]
>;

export default defineComponent({
  setup() {
    const loading = ref(false);
    const store = useMedicalInstitutionList();

    onBeforeMount(async () => {
      loading.value = true;
      await store.fetchMedicalInstitutions();
      loading.value = false;

      // アンカースクロール
      await nextTick();
      if (location.hash) {
        const target = document.querySelector(
          `[data-anchor="${location.hash.replace(/^#/, '')}"]`,
        );
        const header = document.querySelector('#js-header');
        if (target && header) {
          window.scrollTo({
            top: target.getBoundingClientRect().top - header.clientHeight,
            behavior: 'smooth',
          });
        }
      }
    });

    const prefectureGroupedMedicalInstitutions = computed(() => {
      return store.medicalInstitutions.reduce((acc, current) => {
        const prefecture = current.prefecture;
        if (!acc[prefecture]) acc[prefecture] = [];
        acc[prefecture].push(current);
        return acc;
      }, {} as PrefectureGroupedMedicalInstitutions);
    });

    const lastUpdatedAt = computed(() => formatByString(store.lastUpdatedAt));
    const hasMedicalInstitutions = computed(
      () => store.medicalInstitutions.length > 0,
    );

    return {
      loading,
      prefectureGroupedMedicalInstitutions,
      lastUpdatedAt,
      hasMedicalInstitutions,
    };
  },
});
</script>

<style scoped>
.medical_institution-panel {
  padding: 60px calc(20 / 375 * 100vw) 170px;
  background: var(--white);
  @media (--md) {
    padding: 60px;
  }
}

.medical_institution-list {
  @media (--lg) {
    display: grid;
    grid-template-columns: repeat(2, calc(50% - 30px));
    column-gap: 60px;
  }

  .medical_institution-item {
    margin-bottom: 50px;

    .medical_institution-prefecture {
      font-size: 20px;
      font-weight: bold;
      color: var(--color-text-2);
    }
  }

  .medical_institution-list-child {
    margin-top: 30px;

    .medical_institution-child-item {
      &:not(:last-child) {
        margin-bottom: 20px;
      }

      .medical_institution-name {
        font-size: 18px;
        font-weight: bold;
        line-height: 1.67;
        margin-bottom: 10px;
      }

      .medical_institution-address {
        font-size: 16px;
        line-height: 1.63;
      }
    }
  }
}
.medical_institution-label {
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;

  .medical_institution-label-item {
    font-size: 16px;
    color: var(--color-primary-2);
    width: 160px;
    height: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;

    &:not(:last-child) {
      margin-right: 15px;
    }

    &.bg-green {
      background: var(--color-primary-7);
    }

    &.bg-yellow {
      background: var(--color-tertiary-2);
    }
  }
}
</style>
